<!--
    PANEL ONLY. The MedicAI Network tab (settings-container) holds the heading, the hint and the one accordion this sits
    in, beside the referral network's panels.

    ONE section, COLLEAGUES & THEATRE TEAM (owner, 2026-09-22), in place of the Colleagues, Anaesthetists and Surgical
    assistants panels. It is still ONE list of people with ROLE FLAGS (tasks/theatre-booking-and-procedure-lifecycle.md
    §5.2): a person is entered once and can be a colleague you write to, an anaesthetist and a surgical assistant at once.
    Each chip takes the colour and icon of the person's main role (anaesthetist, then surgical assistant, then colleague),
    with a small icon for a second theatre role. The filter chips are the roles, and also the key to the colours.
-->
<mat-expansion-panel [expanded]="true" data-test="section-contacts">
    <mat-expansion-panel-header>
        <mat-panel-title>
            COLLEAGUES &amp; THEATRE TEAM
            <mat-icon class="expansion-header-icon" (click)="openAddColleagueDialog($event)"
                matTooltip="Add a colleague, anaesthetist or surgical assistant" color="primary"
                tutorialHighlightTarget="NETWORK" data-test="add-contact">add</mat-icon>
        </mat-panel-title>
    </mat-expansion-panel-header>

    <p class="hint cs-panel-hint">The colleagues you write to, and the anaesthetists and surgical assistants you book for
        theatre. One person can be all three.</p>

    <p class="hint cs-empty" *ngIf="!colleagues.length">{{ rosterEmptyHint('all') }}</p>

    <ng-container *ngIf="colleagues.length">
        <!-- The roles, each with its count: also the key to the chip colours below. Empty roles are left out. -->
        <mat-chip-listbox class="cs-filters" aria-label="Show one kind of contact" data-test="contact-filters"
            (change)="setKindFilter($event.value)">
            <ng-container *ngFor="let f of kindFilterOptions; trackBy: trackByKey">
                <!-- Surgical assistants follow the practice's speciality, as their panel always did. -->
                <ng-container *ngIf="f.key !== 'assistant'; else assistantFilter">
                    <mat-chip-option [value]="f.key" [selected]="activeKind === f.key" [ngClass]="'cs-filter cs-kind--' + f.key"
                        [attr.data-test]="'contact-filter-' + f.key">
                        <mat-icon matChipAvatar *ngIf="f.key !== 'all'">{{ kindIcon[f.key] }}</mat-icon>
                        {{ f.label }}<span class="cs-filter__count" *ngIf="f.key !== 'all'">{{ f.count }}</span>
                    </mat-chip-option>
                </ng-container>
                <ng-template #assistantFilter>
                    <ng-container *appGroupSpecialtyHideStructural="{ specialties: nonAssistantSpecialities, condition: 'exclude' }">
                        <mat-chip-option [value]="f.key" [selected]="activeKind === f.key" [ngClass]="'cs-filter cs-kind--' + f.key"
                            [attr.data-test]="'contact-filter-' + f.key">
                            <mat-icon matChipAvatar>{{ kindIcon[f.key] }}</mat-icon>
                            {{ f.label }}<span class="cs-filter__count">{{ f.count }}</span>
                        </mat-chip-option>
                    </ng-container>
                </ng-template>
            </ng-container>
        </mat-chip-listbox>

        <div class="colleagues-toolbar">
            <mat-form-field class="colleague-search-field" appearance="outline" *ngIf="showSearch">
                <mat-label>Search</mat-label>
                <input matInput [ngModel]="colleagueSearchQuery" (ngModelChange)="setSearchQueryFor('all', $event)"
                    placeholder="Search by name, speciality, or email" data-test="contact-search">
                <mat-icon matSuffix *ngIf="!colleagueSearchQuery">search</mat-icon>
                <mat-icon matSuffix *ngIf="colleagueSearchQuery" (click)="clearSearchFor('all')" class="clickable">close</mat-icon>
            </mat-form-field>
            <span class="cs-toolbar-spacer" *ngIf="!showSearch"></span>
            <button mat-icon-button class="colleague-view-toggle-btn" [class.colleague-view-toggle-btn--no-search]="!showSearch"
                (click)="toggleColleagueViewMode()"
                [matTooltip]="colleagueViewMode === 'chips' ? 'Switch to list view' : 'Switch to chip view'"
                [attr.aria-label]="colleagueViewMode === 'chips' ? 'Switch to list view' : 'Switch to chip view'">
                <mat-icon>{{ colleagueViewMode === 'chips' ? 'view_list' : 'grid_view' }}</mat-icon>
            </button>
        </div>

        <ng-container *ngIf="colleagueViewMode === 'chips' && visibleContacts.length">
            <mat-form-field class="full-width" appearance="outline">
                <mat-label>{{ activeKindLabel }}</mat-label>
                <mat-chip-grid #chipGrid aria-label="Your colleagues and theatre team">
                    <mat-chip-row *ngFor="let colleague of pagedContacts; trackBy: trackById" appTouchEdit
                        (dblclick)="editColleague(colleague)" [editable]="false"
                        [matTooltip]="getTooltipText(colleague)" [ngClass]="'cs-chip cs-kind--' + kindOf(colleague)"
                        [attr.aria-label]="roleSummary(colleague) + ': ' + fullName(colleague)"
                        [attr.data-test]="'contact-chip-' + kindOf(colleague)">
                        <mat-icon matChipAvatar>{{ kindIcon[kindOf(colleague)] }}</mat-icon>
                        {{ fullName(colleague) }}
                        <!-- A second theatre role, as its own small icon in its own colour. -->
                        <mat-icon class="cs-extra-role cs-kind--assistant"
                            *ngIf="kindOf(colleague) === 'anaesthetist' && colleague.isSurgicalAssistant"
                            aria-hidden="true">{{ kindIcon.assistant }}</mat-icon>
                        <button matChipRemove (click)="removeColleague(colleague)" [attr.aria-label]="'Remove ' + fullName(colleague)">
                            <mat-icon>cancel</mat-icon>
                        </button>
                    </mat-chip-row>
                </mat-chip-grid>
                <input [matChipInputFor]="chipGrid" disabled />
                <mat-hint>Double click, or tap and hold, a contact to edit them. Hover for their details.</mat-hint>
            </mat-form-field>
        </ng-container>

        <ng-container *ngIf="colleagueViewMode === 'grid' && visibleContacts.length">
            <div class="colleagues-list-wrap">
                <mat-list class="colleagues-list">
                    <mat-list-item class="colleague-list-item auto-height" *ngFor="let colleague of pagedContacts; let last = last; trackBy: trackById"
                        appTouchEdit (dblclick)="editColleague(colleague)" [matTooltip]="getTooltipText(colleague)">
                        <div matLine class="colleague-list-title">
                            <span class="cs-kind-tag" [ngClass]="'cs-kind--' + kindOf(colleague)">
                                <mat-icon>{{ kindIcon[kindOf(colleague)] }}</mat-icon>{{ kindLabel[kindOf(colleague)] }}</span>
                            {{ fullName(colleague) }}
                        </div>
                        <div matLine class="colleague-list-secondary" *ngIf="colleague.speciality || colleague.email">{{ colleague.speciality }}{{ colleague.speciality && colleague.email ? ' · ' : '' }}{{ colleague.email }}</div>
                        <div matLine class="colleague-list-roles" *ngIf="roleLabels(colleague).length">{{ roleLabels(colleague).join(' · ') }}{{ colleague.practiceNumber ? ' · PR ' + colleague.practiceNumber : '' }}</div>
                        <div class="colleague-list-actions">
                            <button mat-icon-button (click)="editColleague(colleague); $event.stopPropagation()" matTooltip="Edit">
                                <mat-icon>edit</mat-icon>
                            </button>
                            <button mat-icon-button (click)="removeColleague(colleague); $event.stopPropagation()" matTooltip="Remove">
                                <mat-icon>delete</mat-icon>
                            </button>
                        </div>
                        <mat-divider *ngIf="!last"></mat-divider>
                    </mat-list-item>
                </mat-list>
            </div>
        </ng-container>

        <!-- Both views page: an imported network has hundreds of people. -->
        <div class="colleagues-pagination" *ngIf="contactPages > 1" data-test="contact-pages">
            <button mat-button [disabled]="contactPageIndex === 0" (click)="setContactPage(contactPageIndex - 1)">Previous</button>
            <span class="colleagues-pagination-label">Page {{ contactPageIndex + 1 }} of {{ contactPages }}</span>
            <button mat-button [disabled]="contactPageIndex >= contactPages - 1" (click)="setContactPage(contactPageIndex + 1)">Next</button>
        </div>

        <p class="colleagues-empty-hint" *ngIf="!visibleContacts.length" data-test="contact-no-match">
            {{ visibleEmptyHint }} <a href="" (click)="clearContactFilters($event)">Show everyone</a>
        </p>
    </ng-container>
</mat-expansion-panel>

<ng-template #addColleagueDialog>
    <h1 mat-dialog-title>{{ isNewContact ? 'Add a contact' : 'Edit ' + (fullName(editingColleague) || 'contact') }}</h1>
    <div mat-dialog-content>
        <!--
            WHAT KIND first (owner, 2026-09-22): one dialog for all three. The kind sets the person's main role flag; the
            fields below are the same person record for every kind, and only what is required or hinted changes.
        -->
        <div class="cs-kind-picker" role="group" aria-label="What kind of contact">
            <div class="cs-kind-picker__label">What kind of contact?</div>
            <mat-chip-listbox [value]="dialogKind" (change)="setDialogKind($event.value)" aria-label="Kind of contact"
                data-test="contact-kind">
                <mat-chip-option value="colleague" [ngClass]="'cs-filter cs-kind--colleague'" data-test="contact-kind-colleague">
                    <mat-icon matChipAvatar>{{ kindIcon.colleague }}</mat-icon>Colleague</mat-chip-option>
                <mat-chip-option value="anaesthetist" [ngClass]="'cs-filter cs-kind--anaesthetist'" data-test="contact-kind-anaesthetist">
                    <mat-icon matChipAvatar>{{ kindIcon.anaesthetist }}</mat-icon>Anaesthetist</mat-chip-option>
                <ng-container *appGroupSpecialtyHideStructural="{ specialties: nonAssistantSpecialities, condition: 'exclude' }">
                    <mat-chip-option value="assistant" [ngClass]="'cs-filter cs-kind--assistant'" data-test="contact-kind-assistant">
                        <mat-icon matChipAvatar>{{ kindIcon.assistant }}</mat-icon>Surgical assistant</mat-chip-option>
                </ng-container>
            </mat-chip-listbox>
            <p class="hint cs-kind-picker__hint">{{ kindHint[dialogKind] }}</p>
        </div>
        <div class="p-t-10 field-column">
            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Title</mat-label>
                <mat-select [(ngModel)]="editingColleague.title" required>
                    <mat-option *ngFor="let title of titles" [value]="title.value">
                        {{ title.text }}
                    </mat-option>
                </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Name</mat-label>
                <input matInput [(ngModel)]="editingColleague.name" placeholder="Name" type="text" required>
            </mat-form-field>
            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Surname</mat-label>
                <input matInput [(ngModel)]="editingColleague.surname" placeholder="Surname" type="text" required>
            </mat-form-field>
            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Speciality</mat-label>
                <mat-select [(ngModel)]="editingColleague.speciality">
                    <mat-option *ngFor="let speciality of specialities" [value]="speciality.value">
                        {{ speciality.text }}
                    </mat-option>
                </mat-select>
            </mat-form-field>

            <!--
                A second theatre role. The flags stay independent (the same person is very often both an anaesthetist you
                book and a colleague you write to); the kind above is the main one, this adds the other. Ticking
                Anaesthetist DEFAULTS an empty speciality to Anesthetics; it never overwrites one you chose.
            -->
            <div class="colleague-role-flags" *ngIf="dialogKind !== 'colleague'">
                <mat-checkbox *ngIf="dialogKind === 'assistant'" [checked]="!!editingColleague.isAnaesthetist"
                    (change)="onAnaesthetistToggle($event.checked)" data-test="also-anaesthetist">Also an anaesthetist</mat-checkbox>
                <ng-container *ngIf="dialogKind === 'anaesthetist'">
                    <ng-container *appGroupSpecialtyHideStructural="{ specialties: nonAssistantSpecialities, condition: 'exclude' }">
                        <mat-checkbox [checked]="!!editingColleague.isSurgicalAssistant"
                            (change)="onSurgicalAssistantToggle($event.checked)" data-test="also-assistant">Also a surgical assistant</mat-checkbox>
                    </ng-container>
                </ng-container>
            </div>

            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Email{{ emailIsRequired ? '' : ' (optional)' }}</mat-label>
                <input matInput [(ngModel)]="editingColleague.email" placeholder="Email" type="email"
                    [required]="emailIsRequired">
                <mat-hint *ngIf="!emailIsRequired">Optional for a theatre-roster entry. Needed only to email letters.</mat-hint>
            </mat-form-field>
            <p class="colleague-validation-error" *ngFor="let error of colleagueValidationErrors">
                <mat-icon class="colleague-validation-icon">error_outline</mat-icon>
                <span>{{ error }}</span>
            </p>

            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Practice Number (BHF)</mat-label>
                <input matInput [(ngModel)]="editingColleague.practiceNumber" placeholder="Practice Number" type="text">
                <mat-hint *ngIf="editingColleague.isSurgicalAssistant">A surgical assistant is billed — without a practice number the assistant line on a claim cannot be completed.</mat-hint>
            </mat-form-field>

            <!--
                HPCSA number. A DIFFERENT identifier from the BHF practice number above, and the
                labels say so explicitly: the theatre-booking form prints the PRACTICE number, and
                somebody typing one into the other field would put the wrong number on a document
                that leaves the practice.

                Optional, and no validation beyond trimming — this is a capture field, and a
                practice half-filling it is the expected state, not an error.
            -->
            <mat-form-field appearance="outline" class="full-width">
                <mat-label>HPCSA Number (optional)</mat-label>
                <input matInput [(ngModel)]="editingColleague.mpNumber" placeholder="e.g. MP0123456" type="text">
                <mat-hint>The practitioner's HPCSA registration number — not the BHF practice number above.</mat-hint>
            </mat-form-field>

            <!-- <mat-form-field appearance="outline" class="full-width">
                <mat-label>Contact Number</mat-label>
                <input matInput [(ngModel)]="editingColleague.contactNumber" placeholder="Contact Number" type="tel">
            </mat-form-field> -->

            <app-intl-phone-input [preferredCountries]="['za']" [providedValue]="editingColleague.contactNumber"
                placeholder="Contact Number" (valueChange)="editingColleague.contactNumber = $event"
                name="contactNumber" classList="full-width"></app-intl-phone-input>
        </div>
    </div>
    <div mat-dialog-actions class="dialog-actions">
        <button mat-raised-button color="primary" (click)="saveColleague()">Save</button>
    </div>

</ng-template>
